import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  PlusIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";

import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import PlayerImport from "../components/PlayerImport";
import PlayerList from "../components/PlayerList";
import { apiService } from "../services/api";

const PlayerManagement = () => {
  const { clubId } = useParams();
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [playerLimitInfo, setPlayerLimitInfo] = useState(null); // { current, limit } — null until loaded
  const [featureFlags, setFeatureFlags] = useState(null); // null = all enabled (not yet loaded)
  const [newPlayer, setNewPlayer] = useState({
    name: "",
    number: "",
    position: "",
    grade: "",
    email: "",
  });

  const loadPlayers = useCallback(async () => {
    try {
      const playersData = await apiService.getClubPlayers(clubId);
      setPlayers(playersData);
    } catch (error) {
      console.error("Error loading players:", error);
      toast.error("Failed to load players");
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    loadPlayers();
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => {
        setPlayerLimitInfo({ current: limits.players.current, limit: limits.players.limit });
        setFeatureFlags(limits.feature_flags ?? null);
      })
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [loadPlayers, clubId]);

  const handleImportComplete = (importedPlayers) => {
    setPlayers((prev) => [...prev, ...importedPlayers]);
    setShowImport(false);
    toast.success(`${importedPlayers.length} players imported successfully!`);
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      const playerData = {
        ...newPlayer,
        clubId,
        number: parseInt(newPlayer.number),
        grade: parseInt(newPlayer.grade),
      };

      const createdPlayer = await apiService.createPlayer(playerData);
      setPlayers((prev) => [...prev, createdPlayer]);
      setShowAddPlayer(false);
      setNewPlayer({
        name: "",
        number: "",
        position: "",
        grade: "",
        email: "",
      });
      toast.success("Player added successfully!");
    } catch (error) {
      toast.error("Failed to add player");
    }
  };

  const handleDeletePlayer = async () => {
    if (!playerToDelete) return;

    setDeleting(true);
    try {
      await apiService.deletePlayer(playerToDelete.id);
      setPlayers((prev) => prev.filter((p) => p.id !== playerToDelete.id));
      setShowDeleteModal(false);
      setPlayerToDelete(null);
      toast.success("Player deleted successfully");
    } catch (error) {
      toast.error("Failed to delete player");
    }
    setDeleting(false);
  };

  const handleDeletePlayerClick = (player) => {
    setPlayerToDelete(player);
    setShowDeleteModal(true);
  };

  const handleExportPlayers = () => {
    const csvContent = [
      ["Name", "Number", "Position", "Grade", "Email"],
      ...players.map((player) => [
        player.name,
        player.number,
        player.position || "",
        player.grade || "",
        player.email || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "players.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Players exported successfully!");
  };

  const filteredPlayers = players.filter(
    (player) =>
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.number.toString().includes(searchTerm) ||
      (player.position &&
        player.position.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Player Management
          </h1>
          <p className="text-gray-600">
            {playerLimitInfo?.current ?? players.length}
            {playerLimitInfo?.limit != null ? ` / ${playerLimitInfo.limit}` : ""} players
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExportPlayers}>
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {(() => {
            const limit = playerLimitInfo?.limit;
            const current = playerLimitInfo?.current ?? players.length;
            const atLimit = limit != null && current >= limit;
            const tooltip = atLimit
              ? `Your plan allows ${limit} player${limit === 1 ? "" : "s"}. Upgrade your plan to add more.`
              : undefined;
            return (
              <>
                <div className="relative group">
                  <Button
                    variant="secondary"
                    onClick={atLimit ? undefined : () => setShowImport(true)}
                    disabled={atLimit}
                  >
                    <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                  {atLimit && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      {tooltip}
                    </div>
                  )}
                </div>
                <div className="relative group">
                  <Button
                    onClick={atLimit ? undefined : () => setShowAddPlayer(true)}
                    disabled={atLimit}
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Player
                  </Button>
                  {atLimit && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      {tooltip}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Search players by name, number, or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Import Modal */}
      {showImport && (
        <Modal
          isOpen={showImport}
          onClose={() => setShowImport(false)}
          title="Import Players"
          size="lg"
        >
          <PlayerImport
            clubId={clubId}
            onComplete={handleImportComplete}
            onCancel={() => setShowImport(false)}
            featureFlags={featureFlags}
          />
        </Modal>
      )}

      {/* Add Player Modal */}
      <Modal
        isOpen={showAddPlayer}
        onClose={() => setShowAddPlayer(false)}
        title="Add New Player"
      >
        <form onSubmit={handleAddPlayer} className="space-y-4">
          <Input
            placeholder="Player Name"
            value={newPlayer.name}
            onChange={(e) =>
              setNewPlayer({ ...newPlayer, name: e.target.value })
            }
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              placeholder="Jersey Number"
              value={newPlayer.number}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, number: e.target.value })
              }
              required
            />
            <Input
              placeholder="Position"
              value={newPlayer.position}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, position: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              type="number"
              placeholder="Grade (9-12)"
              value={newPlayer.grade}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, grade: e.target.value })
              }
            />
            <Input
              type="email"
              placeholder="Email (optional)"
              value={newPlayer.email}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, email: e.target.value })
              }
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowAddPlayer(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Add Player</Button>
          </div>
        </form>
      </Modal>

      {/* Players List */}
      <Card>
        <div className="p-6">
          <PlayerList
            players={filteredPlayers}
            onUpdate={loadPlayers}
            onDelete={handleDeletePlayerClick}
          />
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeletePlayer}
        onCancel={() => {
          setShowDeleteModal(false);
          setPlayerToDelete(null);
        }}
        title="Delete Player"
        message={`Are you sure you want to delete "${playerToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleting}
        type="danger"
      />
    </div>
  );
};

export default PlayerManagement;
